import { formatStringToDate } from "@/utils/formatter";

interface Offset {
  days?: number;
  months?: number;
  years?: number;
}

function createRelativeDate({ days = 0, months = 0, years = 0 }: Offset, add = true): Date {
  const date = new Date();
  date.setDate(date.getDate() + (add ? days : -days));
  date.setMonth(date.getMonth() + (add ? months : -months));
  date.setFullYear(date.getFullYear() + (add ? years : -years));
  return date;
}

function createDateBefore(offset: Offset): Date {
  const date = new Date();
  date.setDate(date.getDate() - (offset.days || 0));
  date.setMonth(date.getMonth() - (offset.months || 0));
  date.setFullYear(date.getFullYear() - (offset.years || 0));
  return date;
}

/**
 * Validates that the date is within the `offset` period
 * (e.g., from 2 years ago to 1 month ahead).
 * EXAMPLE
 * createDateRangeRule({ years: 2 }, { months: 1 }, this.$tc("errors.date"))
 */
export function createDateRangeRule(
  minOffset: Offset,
  maxOffset: Offset,
  errorMessage = "Out of range"
) {
  return (v: string): true | string => {
    if (!v) return true;

    const date = formatStringToDate(v);
    const min = createRelativeDate(minOffset, false);
    const max = createRelativeDate(maxOffset, true);

    if (date < min || date > max) return errorMessage;

    return true;
  };
}

/**
 * Validates that the input date is within the last `offset` period
 * (e.g. within the last 2 years, until yesterday).
 * EXAMPLE
 * createDateRangeRuleWithinLast({ years: 2 }, this.$tc("errors.date")),
 */
export function createDateRangeRuleWithinLast(
  offset: Offset,
  errorMessage = "Date must be within the last allowed period"
) {
  return (v: string): true | string => {
    if (!v) return true;

    const date = formatStringToDate(v);
    const lowerBound = createDateBefore(offset); // e.g., 2 years ago
    const upperBound = new Date(); // today
    upperBound.setDate(upperBound.getDate() - 1); // yesterday

    if (date < lowerBound || date > upperBound) {
      return errorMessage;
    }

    return true;
  };
}
